import React, { useEffect } from 'react'
import { FieldErrors, UseFormSetValue, useForm } from 'react-hook-form'
import { useRouter } from 'next/router'
import analytics from '@/shared-utils/analytics'
import { ANALYTICS_CATEGORIES, INVALID } from '@/constants/analytics'
import { yupResolver } from '@hookform/resolvers/yup'
import { PackagesFormStateType, MakeWatchObject } from '../types'

type ChildrenFunctionArgs = {
  setValue: UseFormSetValue<PackagesFormStateType>
  errors: FieldErrors<PackagesFormStateType>
  // eslint-disable-next-line @typescript-eslint/ban-types
  register: Function
  watchState: MakeWatchObject<PackagesFormStateType>
}

type FormContainerProps = {
  children: (args: ChildrenFunctionArgs) => JSX.Element
  initialState: PackagesFormStateType
  onSubmit: (data: PackagesFormStateType) => void
  searchFormSchema: any
  fields: string[]
}

export default function FormContainer({
  children,
  initialState,
  onSubmit,
  fields,
  searchFormSchema
}: FormContainerProps) {
  const {
    register,
    unregister,
    setValue,
    clearErrors,
    formState,
    handleSubmit,
    watch
  } = useForm<PackagesFormStateType>({
    defaultValues: initialState,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange'
  })

  const { errors } = formState

  useEffect(() => {
    fields.forEach(name => {
      register(name as any)
    })
    return () => {
      fields.forEach(name => {
        unregister(name as any)
      })
    }
  }, [fields, register, unregister])

  const {
    tripType: watchTripType,
    partialStay: watchPartialStay,
    startLocation: watchStartLocation,
    endLocation: watchEndLocation,
    startDate: watchStartDate,
    endDate: watchEndDate,
    roomCount: watchRoomCount,
    adultCount: watchAdultCount,
    childrenAges: watchChildrenAges,
    childrenCount: watchChildrenCount,
    hotelStartDate: watchHotelStartDate,
    hotelEndDate: watchHotelEndDate,
    pickupCarSomewhere: watchPickupCarSomewhere,
    pickupCarIncludeAirport: watchPickupCarIncludeAirport,
    rcLocation: watchRcLocation,
    rcPickupDate: watchRcPickupDate,
    rcDropoffDate: watchRcDropoffDate,
    rcPickupTime: watchRcPickupTime,
    rcDropoffTime: watchRcDropoffTime,
    gaCategory: watchGaCategory
  } = watch()

  useEffect(() => {
    clearErrors('startLocation')
  }, [clearErrors, watchStartLocation])
  useEffect(() => {
    clearErrors('endLocation')
  }, [clearErrors, watchEndLocation])
  useEffect(() => {
    clearErrors('rcLocation')
  }, [clearErrors, watchRcLocation])
  useEffect(() => {
    clearErrors('startDate')
  }, [clearErrors, watchStartDate])
  useEffect(() => {
    clearErrors('endDate')
  }, [clearErrors, watchEndDate])
  useEffect(() => {
    clearErrors('hotelStartDate')
  }, [clearErrors, watchHotelStartDate, watchPartialStay])
  useEffect(() => {
    clearErrors('hotelEndDate')
  }, [clearErrors, watchHotelEndDate, watchPartialStay])
  useEffect(() => {
    clearErrors('rcPickupDate')
  }, [clearErrors, watchRcPickupDate])
  useEffect(() => {
    clearErrors('rcDropoffDate')
  }, [clearErrors, watchRcDropoffDate])
  useEffect(() => {
    clearErrors('isInfantOnInternationalTravel' as any)
  }, [clearErrors, watchStartLocation, watchEndLocation, watchChildrenAges])
  useEffect(() => {
    clearErrors('isChildrenAgeProvided' as any)
  }, [clearErrors, watchChildrenCount, watchChildrenAges])
  useEffect(() => {
    clearErrors('isRoomCompacityReached' as any)
  }, [
    clearErrors,
    watchAdultCount,
    watchChildrenCount,
    watchTripType,
    watchRoomCount
  ])
  useEffect(() => {
    if (watchPickupCarSomewhere) clearErrors('rcDropoffDate')
  }, [clearErrors, watchPickupCarSomewhere])
  const { pathname } = useRouter()
  const ANALYTICS_CATEGORY = ANALYTICS_CATEGORIES[pathname]

  useEffect(() => {
    setValue('gaCategory', ANALYTICS_CATEGORY)
  }, [setValue, ANALYTICS_CATEGORY])

  useEffect(() => {
    if (formState.submitCount && !formState.isValid) {
      analytics.fireEvent({
        category: ANALYTICS_CATEGORY,
        action: 'VP Search Button',
        label: INVALID
      })
    }
  }, [formState.isValid, formState.submitCount, ANALYTICS_CATEGORY])

  return (
    <form aria-label="packages-search-form" onSubmit={handleSubmit(onSubmit)}>
      {children({
        setValue,
        errors,
        register,
        watchState: {
          watchTripType,
          watchAdultCount,
          watchRoomCount,
          watchChildrenAges,
          watchChildrenCount,
          watchHotelStartDate,
          watchHotelEndDate,
          watchStartDate,
          watchEndDate,
          watchPartialStay,
          watchStartLocation,
          watchEndLocation,
          watchPickupCarSomewhere,
          watchPickupCarIncludeAirport,
          watchRcLocation,
          watchRcPickupDate,
          watchRcDropoffDate,
          watchRcPickupTime,
          watchRcDropoffTime,
          watchGaCategory
        }
      })}
    </form>
  )
}
